import { useController, useFormContext } from "react-hook-form";
import type { BidPostData } from "validations";

import {
  ControlledWrapper,
  InputWrapper,
} from "@krea/shared/design-system/input/controlled-wrapper";
import { FieldLabel } from "@krea/shared/design-system/input/field";
import { IntegerInput } from "@krea/shared/design-system/input/integer-input";
import { MoneyInput } from "@krea/shared/design-system/input/money-input";
import { PercentageInput } from "@krea/shared/design-system/input/percentage-input";
import {
  type ISegmentedToggleOption,
  SegmentedToggle,
} from "@krea/shared/design-system/SegmentedToggle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@krea/shared/design-system/Select";
import type { Currency } from "@krea/shared/types/common";

import {
  BuildingCreditPaymentType,
  PaymentFrequency,
} from "@/features/common/enums";
import type { IBuildingCreditBidInput } from "@/features/lender-responses/types/lender-response";
import { capitalizeWithSpaces } from "@/utils";

import DetailsItem from "../../../../details/item";
import {
  AdministrationFeeTooltip,
  administrationFeeLabelText,
} from "../../bidHelpers";
import { MaxFinancingInput } from "./MaxFinancingInput";
import { SetupFeeInput } from "./SetupFeeInput";

const PAYMENT_TYPE_OPTIONS: ISegmentedToggleOption<BuildingCreditPaymentType>[] =
  [
    {
      value: BuildingCreditPaymentType.PAID_UPFRONT,
      label: "Upfront",
      ariaLabel: "Paid upfront",
    },
    {
      value: BuildingCreditPaymentType.EACH_LIFT,
      label: "Each lift",
      ariaLabel: "Paid at each lift",
    },
  ];

interface IFormProps {
  bidFormIndex: number;
  currency: Currency;
  isEditing: boolean;
  isInterestRatePerYearActivated?: boolean;
}

export const BuildingCreditForm = ({
  bidFormIndex,
  currency,
  isEditing,
  isInterestRatePerYearActivated,
}: IFormProps) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<BidPostData<IBuildingCreditBidInput>>();
  const { field: paymentTypeField } = useController({
    control,
    name: `bids.${bidFormIndex}.paymentType`,
  });
  const paymentType =
    paymentTypeField.value ?? BuildingCreditPaymentType.PAID_UPFRONT;
  // Only the fee that applies to the chosen timing is sent, so the other is
  // cleared on switch.
  const handlePaymentTypeChange = (next: BuildingCreditPaymentType) => {
    paymentTypeField.onChange(next);
    setValue(
      next === BuildingCreditPaymentType.EACH_LIFT
        ? `bids.${bidFormIndex}.setupFee`
        : `bids.${bidFormIndex}.liftFeePercentage`,
      null,
    );
  };
  // The exactly-one rule reports on maxFinancingLtc when both are set.
  const bidErrors = errors.bids?.[bidFormIndex];
  const maxFinancingError =
    bidErrors?.maxFinancingLtv ?? bidErrors?.maxFinancingLtc;

  return (
    <div className="flex w-full flex-col">
      <div className="w-full space-y-4">
        <DetailsItem
          title={
            isEditing ? "Update building credit terms" : "Building credit terms"
          }
          className="mb-4"
        >
          <div className="w-full space-y-4">
            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.amount`}
              label="Credit limit"
              required
              render={({ field }) => (
                <MoneyInput
                  {...field}
                  currency={currency}
                  id={field.name}
                  aria-required
                />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.paybackTimeMonths`}
              label="Loan term"
              required
              render={({ field }) => (
                <IntegerInput
                  {...field}
                  id={field.name}
                  aria-required
                  suffix="Months"
                  placeholder="Number of months"
                />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.lenderMonthlyInterest`}
              label={
                isInterestRatePerYearActivated
                  ? "Interest per year (%)"
                  : "Interest per month (%)"
              }
              required
              render={({ field }) => (
                <PercentageInput
                  {...field}
                  id={field.name}
                  aria-required
                  placeholder='Value in "%"'
                />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.paymentFrequency`}
              label="Interest payment frequency"
              required
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  required
                >
                  <SelectTrigger id={field.name} aria-required>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(PaymentFrequency).map((value) => (
                      <SelectItem key={value} value={value}>
                        {capitalizeWithSpaces(value)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />

            <InputWrapper label="Fee paid" required>
              <SegmentedToggle
                aria-label="Fee paid"
                cycleOnClick={false}
                options={PAYMENT_TYPE_OPTIONS}
                value={paymentType}
                onValueChange={handlePaymentTypeChange}
              />
            </InputWrapper>

            {paymentType === BuildingCreditPaymentType.EACH_LIFT ? (
              <ControlledWrapper
                control={control}
                name={`bids.${bidFormIndex}.liftFeePercentage`}
                label="Lift fee (%)"
                required
                render={({ field }) => (
                  <PercentageInput
                    {...field}
                    id={field.name}
                    aria-required
                    placeholder="e.g. 2"
                  />
                )}
              />
            ) : (
              <ControlledWrapper
                control={control}
                name={`bids.${bidFormIndex}.setupFee`}
                label="Setup fee"
                required
                render={({ field }) => (
                  <SetupFeeInput
                    field={field}
                    bidFormIndex={bidFormIndex}
                    currency={currency}
                  />
                )}
              />
            )}

            <InputWrapper
              label="Max financing (%)"
              htmlFor={`max-financing-${bidFormIndex}`}
              required
              errors={maxFinancingError ? [maxFinancingError] : undefined}
            >
              <MaxFinancingInput
                control={control}
                bidFormIndex={bidFormIndex}
              />
              <p className="text-helper">
                LTV = % of property value · LTC = % of project cost
              </p>
            </InputWrapper>
          </div>
        </DetailsItem>

        <DetailsItem title="" className="mb-4">
          <div className="w-full space-y-4">
            <span className="mb-2 ml-3 block text-sm italic text-gray-600">
              Here you can explain other costs & qualitative information about
              your offer
            </span>

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.limitFeePerYearPercent`}
              label="Limit fee per year (%)"
              render={({ field }) => (
                <PercentageInput
                  {...field}
                  id={field.name}
                  placeholder='Value in "%"'
                />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.administrationFees`}
              render={({ field }) => (
                <>
                  <div className="flex items-center gap-2">
                    <FieldLabel htmlFor={field.name}>
                      {administrationFeeLabelText}
                    </FieldLabel>
                    <AdministrationFeeTooltip />
                  </div>
                  <MoneyInput {...field} currency={currency} id={field.name} />
                </>
              )}
            />
          </div>
        </DetailsItem>
      </div>
    </div>
  );
};
